// components/FeaturesAndFilters.jsx
import React from "react";
import {
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
} from "reactstrap";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { Tooltip } from "react-tooltip";

const FeaturesAndFilters = ({ settings, onUpdateSetting }) => {
  const InfoTooltip = ({ id, content, placement = "top" }) => (
    <>
      <AiOutlineInfoCircle
        data-tooltip-id={id}
        title={content}
        style={{
          marginLeft: "4px",
          cursor: "help",
          color: "#6B7280",
          fontSize: "16px",
          verticalAlign: "middle",
          display: "inline-flex",
          alignItems: "center",
        }}
      />
    </>
  );

  const inputStyle = {
    borderRadius: "6px",
    border: "1px solid #d1d5db",
    fontSize: "14px",
    padding: "8px 12px",
    backgroundColor: "#ffffff",
    transition: "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
  };

  // ✅ Enhanced input handler with focus management according to project specifications
  const handleNumberInputChange = (filterKey, fieldKey, value) => {
    onUpdateSetting(filterKey, {
      ...settings[filterKey],
      [fieldKey]: value,
    });
  };

  const handleKeyDown = (e) => {
    if (
      e.key === "Backspace" &&
      (e.target.value === "" || e.target.value == null)
    ) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <Card
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: "4px",
        height: "100%",
        minHeight: "400px", // Match SeqTypesAndGroups height
      }}
    >
      <CardHeader>
        <div className="d-flex justify-content-between align-items-center">
          <h5
            className="mb-0 fw-semibold d-flex align-items-center"
            style={{ color: "#1a6d51" }}
          >
            <i className="ri-settings-3-line me-2 " />
            Features & Filters
          </h5>
        </div>
      </CardHeader>
      <CardBody>
        <Row>
          <Col>
            {/* Start codons */}
            <FormGroup className="mb-3">
              <Label>Start codons:</Label>
              <div className="mt-2 p-4" 
                   style={{display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 0.12fr))"}}>
                {["AUG","CUG","GUG","UUG","ACG","AAG","AGG","AUC","AUA","AUU"].map((codon) => (
                  <FormGroup check key={codon}>
                    <Input
                      type="checkbox"
                      checked={settings.startCodons.includes(codon)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          onUpdateSetting("startCodons", [
                            ...settings.startCodons,
                            codon,
                          ]);
                        } else {
                          onUpdateSetting(
                            "startCodons",
                            settings.startCodons.filter((c) => c !== codon)
                          );
                        }
                      }}
                      className="form-check-input"
                    />
                    <Label check>{codon}</Label>
                  </FormGroup>
                ))}
              </div>
            </FormGroup>

            {/* Transcripts */}
            <FormGroup className="mb-3">
              <Label>Transcripts:</Label>
              <div className="mt-2">
                {[
                  { value: "prin_trans", label: "Principal" },
                  { value: "all", label: "All" },
                  { value: "custom_trans", label: "Custom" },
                ].map((option) => (
                  <FormGroup
                    check
                    key={option.value}
                    className="form-check-inline"
                  >
                    <Input
                      type="radio"
                      name="transcriptInput"
                      value={option.value}
                      checked={settings.transcriptInput === option.value}
                      onChange={(e) =>
                        onUpdateSetting("transcriptInput", e.target.value)
                      }
                      className="form-check-input"
                    />
                    <Label check>{option.label}</Label>
                  </FormGroup>
                ))}
              </div>
            </FormGroup>

            {/* Custom transcript list */}
            {settings.transcriptInput === "custom_trans" && (
              <FormGroup>
                <Label>Custom:</Label>
                <Input
                  type="textarea"
                  placeholder="ENST00000368885"
                  value={settings.customTranscriptList || ""}
                  onChange={(e) =>
                    onUpdateSetting("customTranscriptList", e.target.value)
                  }
                  onKeyDown={handleKeyDown}
                  style={inputStyle}
                  rows="3"
                />
              </FormGroup>
            )}

            <FormGroup check>
              <Input
                type="checkbox"
                checked={settings.emailNotification}
                onChange={(e) =>
                  onUpdateSetting("emailNotification", e.target.checked)
                }
                className="form-check-input"
              />
              <Label check>
                Email notification
                <InfoTooltip
                  id="tt-email"
                  content="Send email when analysis is complete"
                />
              </Label>
            </FormGroup>

          </Col>

          {/* Filters Column */}
          <Col md={6}>
            <Row>
            <Label lg={4} className="mb-3 text-muted">Filters</Label>
            <Label lg={4} className="mb-3 text-muted">Min Limit</Label>
            <Label lg={4} className="mb-3 text-muted">Max Limit</Label>
            </Row>

            {/* Probability */}
            <FormGroup className="mb-3">
              <Row>
                <Label lg={4}>
                  Probability:
                  <InfoTooltip
                    id="tt-probability"
                    content="Set min and max probability"
                  />
                </Label>

                <Col>
                  <Input
                    type="number"
                    step="0.05"
                    min="0"
                    max="1"
                    placeholder="Min Limit (0)"
                    value={settings.filters.minProbability || 0.5}
                    onChange={(e) =>
                      handleNumberInputChange(
                        "filters",
                        "minProbability",
                        e.target.value
                      )
                    }
                    onKeyDown={handleKeyDown}
                    style={inputStyle}
                  />
                </Col>
                <Col>
                  <Input
                    type="number"
                    step="0.05"
                    min="0"
                    max="1"
                    placeholder="Max Limit"
                    value={settings.filters.maxProbability || 1}
                    onChange={(e) =>
                      handleNumberInputChange(
                        "filters",
                        "maxProbability",
                        e.target.value
                      )
                    }
                    onKeyDown={handleKeyDown}
                    style={inputStyle}
                  />
                </Col>
              </Row>
            </FormGroup>

            {/* Avg. Read density */}
            <FormGroup className="mb-3">
              <Row>
                <Label lg={4}>
                  Avg. Read Density:
                  <InfoTooltip
                    id="tt-avg-read-density"
                    content="Set min and max avg. read density"
                  />
                </Label>

                <Col>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    placeholder="Min Limit (0)"
                    value={settings.filters.minAvgReadDensity || 0}
                    onChange={(e) =>
                      handleNumberInputChange(
                        "filters",
                        "minAvgReadDensity",
                        e.target.value
                      )
                    }
                    onKeyDown={handleKeyDown}
                    style={inputStyle}
                  />
                </Col>
                <Col>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    placeholder="Max Limit"
                    value={settings.filters.maxAvgReadDensity || 1000000}
                    onChange={(e) =>
                      handleNumberInputChange(
                        "filters",
                        "maxAvgReadDensity",
                        e.target.value
                      )
                    }
                    onKeyDown={handleKeyDown}
                    style={inputStyle}
                  />
                </Col>
              </Row>
            </FormGroup>

            {/* CDS overlap */}
            <FormGroup className="mb-3">
              <Row>
                <Label lg={4}>
                  CDS overlap:
                  <InfoTooltip
                    id="tt-cds-overlap"
                    content="Set CDS overlap range (supports decimal values like 0.2, 0.5, 1.3)"
                  />
                </Label>

                <Col>
                  <Input
                    type="number"
                    step="0.05"
                    min="0"
                    max="1"
                    placeholder="Min Limit (0)"
                    value={settings.filters.minCds || ""}
                    onChange={(e) =>
                      handleNumberInputChange(
                        "filters",
                        "minCds",
                        e.target.value
                      )
                    }
                    onKeyDown={handleKeyDown}
                    style={inputStyle}
                  />
                </Col>
                <Col>
                  <Input
                    type="number"
                    step="0.05"
                    min="0"
                    max="1"
                    placeholder="Max Limit"
                    value={settings.filters.maxCds || ""}
                    onChange={(e) =>
                      handleNumberInputChange(
                        "filters",
                        "maxCds",
                        e.target.value
                      )
                    }
                    onKeyDown={handleKeyDown}
                    style={inputStyle}
                  />
                </Col>
              </Row>
            </FormGroup>

            {/* ORF/Translon Length */}
            <FormGroup className="mb-3">
              <Row>
                <Label lg={4}>
                  ORF/Translon Length:
                  <InfoTooltip
                    id="tt-length"
                    content="Set ORF length range in nucleotides"
                  />
                </Label>
                <Col>
                  <Input
                    type="number"
                    placeholder="Min Limit (30)"
                    value={settings.filters.minLen || ""}
                    onChange={(e) =>
                      handleNumberInputChange(
                        "filters",
                        "minLen",
                        e.target.value
                      )
                    }
                    onKeyDown={handleKeyDown}
                    style={inputStyle}
                  />
                </Col>
                <Col>
                  <Input
                    type="number"
                    placeholder="Max Limit (100000)"
                    value={settings.filters.maxLen || ""}
                    onChange={(e) =>
                      handleNumberInputChange(
                        "filters",
                        "maxLen",
                        e.target.value
                      )
                    }
                    onKeyDown={handleKeyDown}
                    style={inputStyle}
                  />
                </Col>
              </Row>
            </FormGroup>

            {/* Read length */}
            <FormGroup className="mb-3">
              <Row>
                <Label lg={4}>
                  Read length:
                  <InfoTooltip
                    id="tt-read-length"
                    content="Set read length range"
                  />
                </Label>
                <Col>
                  <Input
                    type="number"
                    placeholder="Min Limit"
                    value={settings.filters.minReadLen || 20}
                    onChange={(e) =>
                      handleNumberInputChange(
                        "filters",
                        "minReadLen",
                        e.target.value
                      )
                    }
                    onKeyDown={handleKeyDown}
                    style={inputStyle}
                  />
                </Col>
                <Col>
                  <Input
                    type="number"
                    placeholder="Max Limit"
                    value={settings.filters.maxReadLen || 35}
                    onChange={(e) =>
                      handleNumberInputChange(
                        "filters",
                        "maxReadLen",
                        e.target.value
                      )
                    }
                    onKeyDown={handleKeyDown}
                    style={inputStyle}
                  />
                </Col>
              </Row>
            </FormGroup>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

export default FeaturesAndFilters;
